import type { CompanyQuarter, CompanySeries, DataPanel } from "../types";
import type { EpochCashFlowBenchmark, EpochCashFlowPoint } from "../dataSources/epochCashFlow";

const REQUIRED_COMPANIES = ["alphabet", "microsoft", "amazon", "meta"] as const;
const OPTIONAL_COMPANIES = ["oracle"] as const;
const MIN_TRAIN_QUARTERS = 12;
const MIN_POST_2023_TREND_ROWS = 4;
const REGIME_START = "2023-Q2";
const REGIME_TREND_WEIGHT = 0.5;
const RECENT_WINDOW = 8;
const MIN_CONFORMAL_RESIDUALS = 9;
const INTERVAL_LEVEL = 0.9;
const SIMULATION_PATHS = 1024;

export type FundingPressureModelId =
  | "lag-1"
  | "seasonal-lag-4"
  | "epoch-log-linear"
  | "regime-blend"
  | "seasonal-log-trend";

export type FundingPressureForecastModelId = Exclude<FundingPressureModelId, "epoch-log-linear">;

export interface FundingPressureScenario {
  /** Annualized delta applied only to future cash CapEx. */
  annualCapexGrowthDeltaPct?: number;
  /** Annualized delta applied only to future operating cash flow. */
  annualOcfGrowthDeltaPct?: number;
}

export interface FundingPressureOptions {
  benchmark?: EpochCashFlowBenchmark;
  scenario?: FundingPressureScenario;
}

export interface FundingPressureWatermark {
  capexFiledFirst: string;
  ocfFiledFirst: string;
  availableAt: string;
  ageDays: number;
}

export interface FundingPressureActual {
  cashCapex: number;
  operatingCashFlow: number;
  capexToOcf: number;
  freeCashFlow: number;
  fundingState: "self-funded" | "funding-gap";
  fundingCoveragePct: number;
  financingGap: number;
}

export interface FundingPressureMonitorRow extends FundingPressureActual {
  id: string;
  name: string;
  period: string;
  watermark: FundingPressureWatermark;
}

export interface FundingPressureInterval {
  low: number;
  high: number;
  level: 0.9 | null;
  source: "oos-conformal" | "rmse-fallback";
  calibrated: boolean;
  residualCount: number;
  label: string;
}

export interface FundingPressureScenarioRange {
  low: number;
  high: number;
  source: "scenario-range";
  label: string;
}

export interface FundingPressureForecastPoint {
  period: string;
  horizon: number;
  cashCapex: number;
  operatingCashFlow: number;
  capexToOcf: number;
  freeCashFlow: number;
  ratioInterval: FundingPressureInterval;
  fcfInterval: FundingPressureInterval;
  scenarioRange: FundingPressureScenarioRange;
}

export interface FundingPressureBacktestPoint {
  period: string;
  actual: FundingPressureActual;
  predicted: FundingPressureActual;
  ratioError: number;
  fcfError: number;
  capexLogError: number;
  ocfLogError: number;
}

export interface FundingPressureMetricWindow {
  observations: number;
  ratioRmse: number;
  normalizedRatioRmse: number;
  ratioMae: number;
  ratioMape: number;
  fcfRmse: number;
  fcfMae: number;
  normalizedFcfRmse: number;
  /** Equal-weight joint loss for the ratio and FCF targets. Lower is better. */
  compositeScore: number;
}

export interface FundingPressureBacktestModel {
  id: FundingPressureModelId;
  label: string;
  points: FundingPressureBacktestPoint[];
    metrics: {
      full: FundingPressureMetricWindow;
      recent8: FundingPressureMetricWindow;
      multiHorizon8: FundingPressureMetricWindow;
    };
}

export interface FundingPressureCrossing {
  status: "already-crossed" | "projected" | "censored";
  pointEstimate: string | null;
  censoredAfter: string | null;
  probabilityWithinHorizon: number | null;
  probabilityByQuarter: Array<{ period: string; cumulativeProbability: number | null }>;
  conditionalQuarterInterval90: [string, string] | null;
  simulationPaths: number;
  residualBlocks: number;
  calibrated: boolean;
  explanation: string;
}

export interface FundingPressureExternalBenchmark {
  scope: "five-hyperscaler-aggregate";
  comparableToPanel: boolean;
  panelCompanyCount: number;
  role: "cross-check-only";
  latestObserved: EpochCashFlowPoint;
  trend: EpochCashFlowPoint[];
  warning: string;
}

export interface FundingPressureSliceResult {
  monitor: {
    asOf: string;
    commonPeriod: string;
    companies: FundingPressureMonitorRow[];
    aggregate: FundingPressureActual;
    benchmarkCrossCheck: FundingPressureExternalBenchmark | null;
  };
  forecast: {
    commonPeriod: string;
    selectedModel: FundingPressureForecastModelId;
    baselinePoints: FundingPressureForecastPoint[];
    points: FundingPressureForecastPoint[];
    horizons: {
      quarter1: FundingPressureForecastPoint;
      quarter4: FundingPressureForecastPoint;
      quarter8: FundingPressureForecastPoint;
    };
    crossing: FundingPressureCrossing;
    externalBenchmark: FundingPressureExternalBenchmark | null;
    scenarioImpact: {
      affectsCurrentChart: boolean;
      directTargets: ["cash-capex", "operating-cash-flow"];
      historicalBacktestLocked: true;
      formula: string;
      explanation: string;
    };
  };
  backtest: {
    models: {
      lag1: FundingPressureBacktestModel;
      seasonalLag4: FundingPressureBacktestModel;
      epochLogLinear: FundingPressureBacktestModel;
      regimeBlend: FundingPressureBacktestModel;
      seasonalLogTrend: FundingPressureBacktestModel;
    };
    selectedModel: FundingPressureForecastModelId;
    selection: {
      promotedComplexModel: boolean;
      rule: string;
    };
    historicalScenarioLocked: true;
  };
  methodology: {
    target: string;
    vintagePolicy: string;
    seasonality: string;
    validation: string;
    selection: string;
    interval: string;
    crossing: string;
    scenario: string;
  };
  sources: Array<{
    id: string;
    label: string;
    url: string;
    role: string;
  }>;
}

interface AggregateRow {
  period: string;
  cashCapex: number;
  operatingCashFlow: number;
}

interface ComponentPrediction {
  cashCapex: number;
  operatingCashFlow: number;
}

function finite(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function complete(row: CompanyQuarter): row is CompanyQuarter & {
  capexAsFirstReported: number;
  operatingCashFlowFirstReported: number;
} {
  return finite(row.capexAsFirstReported) && finite(row.operatingCashFlowFirstReported);
}

function nextPeriod(period: string) {
  const match = /^(\d{4})-Q([1-4])$/.exec(period);
  if (!match) throw new Error(`Invalid calendar quarter: ${period}.`);
  const year = Number(match[1]);
  const quarter = Number(match[2]);
  return quarter === 4 ? `${year + 1}-Q1` : `${year}-Q${quarter + 1}`;
}

function quarterOrdinal(period: string) {
  const match = /^(\d{4})-Q([1-4])$/.exec(period);
  if (!match) throw new Error(`Invalid calendar quarter: ${period}.`);
  return Number(match[1]) * 4 + Number(match[2]) - 1;
}

function selectedCompanies(panel: DataPanel) {
  const required = REQUIRED_COMPANIES.map((id) => {
    const company = panel.companies[id];
    if (!company) throw new Error(`Missing required company series: ${id}.`);
    return company;
  });
  const optional = OPTIONAL_COMPANIES.flatMap((id) => panel.companies[id] ? [panel.companies[id]] : []);
  return [...required, ...optional];
}

function completeRows(company: CompanySeries) {
  const sorted = [...company.quarters].sort((left, right) => left.period.localeCompare(right.period));
  const firstComplete = sorted.findIndex(complete);
  if (firstComplete < 0) throw new Error(`${company.id}: no SEC first-reported CapEx/OCF observations.`);
  const rows = sorted.slice(firstComplete);
  rows.forEach((row, index) => {
    if (!complete(row)) {
      throw new Error(`${company.id} ${row.period}: SEC first-reported CapEx and OCF are required.`);
    }
    if (row.capexAsFirstReported < 0 || row.operatingCashFlowFirstReported === 0) {
      throw new Error(`${company.id} ${row.period}: CapEx must be non-negative and OCF must be non-zero.`);
    }
    if (index > 0 && row.period !== nextPeriod(rows[index - 1].period)) {
      throw new Error(`${company.id}: non-contiguous quarter ${rows[index - 1].period} → ${row.period}.`);
    }
  });
  return rows as Array<CompanyQuarter & {
    capexAsFirstReported: number;
    operatingCashFlowFirstReported: number;
  }>;
}

function commonAggregate(companies: CompanySeries[]) {
  const rowsByCompany = new Map(companies.map((company) => [company.id, completeRows(company)]));
  const common = companies
    .map((company) => new Set(rowsByCompany.get(company.id)!.map((row) => row.period)))
    .reduce((left, right) => new Set([...left].filter((period) => right.has(period))));
  const periods = [...common].sort();
  if (periods.length < MIN_TRAIN_QUARTERS + 1) {
    throw new Error(`Funding-pressure model has ${periods.length} common quarters; at least ${MIN_TRAIN_QUARTERS + 1} are required.`);
  }
  periods.forEach((period, index) => {
    if (index > 0 && period !== nextPeriod(periods[index - 1])) {
      throw new Error(`Funding-pressure aggregate is non-contiguous: ${periods[index - 1]} → ${period}.`);
    }
  });
  const aggregate = periods.map((period): AggregateRow => {
    let cashCapex = 0;
    let operatingCashFlow = 0;
    companies.forEach((company) => {
      const row = rowsByCompany.get(company.id)!.find((item) => item.period === period)!;
      cashCapex += row.capexAsFirstReported;
      operatingCashFlow += row.operatingCashFlowFirstReported;
    });
    return { period, cashCapex, operatingCashFlow };
  });
  return { aggregate, rowsByCompany };
}

function actual(cashCapex: number, operatingCashFlow: number): FundingPressureActual {
  if (!finite(cashCapex) || cashCapex < 0 || !finite(operatingCashFlow) || operatingCashFlow === 0) {
    throw new Error("Funding-pressure values require non-negative CapEx and non-zero OCF.");
  }
  return {
    cashCapex,
    operatingCashFlow,
    capexToOcf: cashCapex / operatingCashFlow,
    freeCashFlow: operatingCashFlow - cashCapex,
    fundingState: cashCapex <= operatingCashFlow ? "self-funded" : "funding-gap",
    fundingCoveragePct: operatingCashFlow / Math.max(cashCapex, 1e-12) * 100,
    financingGap: Math.max(0, cashCapex - operatingCashFlow),
  };
}

function filedDate(row: CompanyQuarter, field: "capex" | "ocf") {
  const value = field === "capex"
    ? row.capexFiledFirst ?? row.firstFiled ?? row.filed
    : row.operatingCashFlowFiledFirst ?? row.firstFiled ?? row.filed;
  if (!value) throw new Error(`${row.period}: ${field.toUpperCase()} first-reported filing date is missing.`);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || !Number.isFinite(Date.parse(`${value}T00:00:00Z`))) {
    throw new Error(`${row.period}: invalid ${field.toUpperCase()} first-reported filing date ${value}.`);
  }
  return value;
}

function ageDays(availableAt: string, asOf: string) {
  const start = Date.parse(`${availableAt}T00:00:00Z`);
  const end = Date.parse(asOf.length === 10 ? `${asOf}T00:00:00Z` : asOf);
  if (!Number.isFinite(end)) throw new Error(`Invalid panel asOf: ${asOf}.`);
  const age = Math.floor((end - start) / 86_400_000);
  if (age < 0) throw new Error(`First-reported filing ${availableAt} is after panel asOf ${asOf}.`);
  return age;
}

function solveLinear(matrix: number[][], vector: number[]) {
  const width = matrix.length;
  const augmented = matrix.map((row, index) => [...row, vector[index]]);
  for (let column = 0; column < width; column += 1) {
    let pivot = column;
    for (let row = column + 1; row < width; row += 1) {
      if (Math.abs(augmented[row][column]) > Math.abs(augmented[pivot][column])) pivot = row;
    }
    [augmented[column], augmented[pivot]] = [augmented[pivot], augmented[column]];
    const divisor = augmented[column][column];
    if (Math.abs(divisor) < 1e-12) throw new Error("Funding-pressure regression matrix is singular.");
    for (let item = column; item <= width; item += 1) augmented[column][item] /= divisor;
    for (let row = 0; row < width; row += 1) {
      if (row === column) continue;
      const factor = augmented[row][column];
      for (let item = column; item <= width; item += 1) augmented[row][item] -= factor * augmented[column][item];
    }
  }
  return augmented.map((row) => row[width]);
}

function fitLogLinear(
  rows: AggregateRow[],
  targetPeriod: string,
  field: "cashCapex" | "operatingCashFlow",
  seasonal: boolean,
) {
  const filtered = seasonal ? rows.slice(-24) : rows.filter((row) => row.period >= REGIME_START);
  const minimum = seasonal ? MIN_TRAIN_QUARTERS : MIN_POST_2023_TREND_ROWS;
  if (filtered.length < minimum) throw new Error(`${seasonal ? "Seasonal" : "Epoch-style"} log trend needs ${minimum} training quarters.`);
  const originOrdinal = quarterOrdinal(filtered[0].period);
  const feature = (period: string) => {
    const quarter = Number(period.at(-1));
    return seasonal
      ? [1, quarterOrdinal(period) - originOrdinal, quarter === 2 ? 1 : 0, quarter === 3 ? 1 : 0, quarter === 4 ? 1 : 0]
      : [1, quarterOrdinal(period) - originOrdinal];
  };
  const samples = filtered.map((row) => feature(row.period));
  const targets = filtered.map((row) => Math.log(Math.max(row[field], 1e-9)));
  const width = samples[0].length;
  const matrix = Array.from({ length: width }, () => Array(width).fill(0));
  const vector = Array(width).fill(0);
  samples.forEach((sample, sampleIndex) => {
    sample.forEach((left, leftIndex) => {
      vector[leftIndex] += left * targets[sampleIndex];
      sample.forEach((right, rightIndex) => { matrix[leftIndex][rightIndex] += left * right; });
    });
  });
  for (let index = 1; index < width; index += 1) matrix[index][index] += 1e-8;
  const coefficients = solveLinear(matrix, vector);
  const prediction = feature(targetPeriod).reduce((sum, value, index) => sum + value * coefficients[index], 0);
  return Math.exp(prediction);
}

function fitRegimeLogSlope(rows: AggregateRow[], field: "cashCapex" | "operatingCashFlow") {
  const filtered = rows.filter((row) => row.period >= REGIME_START);
  if (filtered.length < MIN_POST_2023_TREND_ROWS) {
    throw new Error(`Regime blend needs ${MIN_POST_2023_TREND_ROWS} quarters from ${REGIME_START}.`);
  }
  const meanX = (filtered.length - 1) / 2;
  const meanY = filtered.reduce((sum, row) => sum + Math.log(Math.max(row[field], 1e-9)), 0) / filtered.length;
  const numerator = filtered.reduce(
    (sum, row, index) => sum + (index - meanX) * (Math.log(Math.max(row[field], 1e-9)) - meanY),
    0,
  );
  const denominator = filtered.reduce((sum, _row, index) => sum + (index - meanX) ** 2, 0);
  if (denominator <= 0) throw new Error("Funding-pressure regime slope is not identifiable.");
  return numerator / denominator;
}

function predict(history: AggregateRow[], targetPeriod: string, model: FundingPressureModelId): ComponentPrediction {
  if (history.length < MIN_TRAIN_QUARTERS) throw new Error(`${model}: insufficient training history.`);
  if (model === "lag-1") {
    const prior = history.at(-1)!;
    return { cashCapex: prior.cashCapex, operatingCashFlow: prior.operatingCashFlow };
  }
  if (model === "seasonal-lag-4") {
    const priorYear = history.at(-4)!;
    return { cashCapex: priorYear.cashCapex, operatingCashFlow: priorYear.operatingCashFlow };
  }
  if (model === "regime-blend") {
    const prior = history.at(-1)!;
    const horizon = quarterOrdinal(targetPeriod) - quarterOrdinal(prior.period);
    if (horizon < 1) throw new Error(`Regime-blend target ${targetPeriod} must follow ${prior.period}.`);
    return {
      cashCapex: prior.cashCapex * Math.exp(
        fitRegimeLogSlope(history, "cashCapex") * REGIME_TREND_WEIGHT * horizon,
      ),
      operatingCashFlow: prior.operatingCashFlow * Math.exp(
        fitRegimeLogSlope(history, "operatingCashFlow") * REGIME_TREND_WEIGHT * horizon,
      ),
    };
  }
  const seasonal = model === "seasonal-log-trend";
  return {
    cashCapex: fitLogLinear(history, targetPeriod, "cashCapex", seasonal),
    operatingCashFlow: fitLogLinear(history, targetPeriod, "operatingCashFlow", seasonal),
  };
}

function modelLabel(model: FundingPressureModelId) {
  const labels: Record<FundingPressureModelId, string> = {
    "lag-1": "lag-1",
    "seasonal-lag-4": "seasonal lag-4",
    "epoch-log-linear": "Epoch-style post-2023 log-linear",
    "regime-blend": "post-2023 anchored trend / persistence blend",
    "seasonal-log-trend": "log-linear + quarter dummies",
  };
  return labels[model];
}

function metrics(points: FundingPressureBacktestPoint[]): FundingPressureMetricWindow {
  if (!points.length) {
    return {
      observations: 0,
      ratioRmse: 0,
      normalizedRatioRmse: 0,
      ratioMae: 0,
      ratioMape: 0,
      fcfRmse: 0,
      fcfMae: 0,
      normalizedFcfRmse: 0,
      compositeScore: 0,
    };
  }
  const mean = (values: number[]) => values.reduce((sum, value) => sum + value, 0) / values.length;
  const ratioRmse = Math.sqrt(mean(points.map((point) => point.ratioError ** 2)));
  const fcfRmse = Math.sqrt(mean(points.map((point) => point.fcfError ** 2)));
  const ratioScale = mean(points.map((point) => Math.abs(point.actual.capexToOcf)));
  const fcfScale = mean(points.map((point) => Math.abs(point.actual.freeCashFlow)));
  const normalizedRatioRmse = ratioRmse / Math.max(ratioScale, 1e-9);
  const normalizedFcfRmse = fcfRmse / Math.max(fcfScale, 1e-9);
  return {
    observations: points.length,
    ratioRmse,
    normalizedRatioRmse,
    ratioMae: mean(points.map((point) => Math.abs(point.ratioError))),
    ratioMape: mean(points.map((point) => Math.abs(point.ratioError) / Math.max(point.actual.capexToOcf, 1e-9))) * 100,
    fcfRmse,
    fcfMae: mean(points.map((point) => Math.abs(point.fcfError))),
    normalizedFcfRmse,
    compositeScore: (normalizedRatioRmse + normalizedFcfRmse) / 2,
  };
}

function buildBacktest(rows: AggregateRow[], model: FundingPressureModelId): FundingPressureBacktestModel {
  const firstTarget = rows.findIndex((row, index) =>
    index >= MIN_TRAIN_QUARTERS
    && row.period >= "2024-Q1"
    && rows.slice(0, index).filter((item) => item.period >= REGIME_START).length >= MIN_POST_2023_TREND_ROWS);
  if (firstTarget < 0) throw new Error("No fair rolling-origin window is available for all funding-pressure models.");
  const points = rows.slice(firstTarget).map((row, offset): FundingPressureBacktestPoint => {
    const targetIndex = firstTarget + offset;
    const prediction = predict(rows.slice(0, targetIndex), row.period, model);
    const observed = actual(row.cashCapex, row.operatingCashFlow);
    const estimated = actual(prediction.cashCapex, prediction.operatingCashFlow);
    return {
      period: row.period,
      actual: observed,
      predicted: estimated,
      ratioError: estimated.capexToOcf - observed.capexToOcf,
      fcfError: estimated.freeCashFlow - observed.freeCashFlow,
      capexLogError: Math.log(observed.cashCapex / Math.max(estimated.cashCapex, 1e-9)),
      ocfLogError: Math.log(observed.operatingCashFlow / Math.max(estimated.operatingCashFlow, 1e-9)),
    };
  });
  return {
    id: model,
    label: modelLabel(model),
    points,
    metrics: {
      full: metrics(points),
      recent8: metrics(points.slice(-Math.min(RECENT_WINDOW, points.length))),
      multiHorizon8: metrics(buildMultiHorizonBacktestPoints(rows, model)),
    },
  };
}

function selectModel(
  lag1: FundingPressureBacktestModel,
  seasonalLag4: FundingPressureBacktestModel,
  regimeBlend: FundingPressureBacktestModel,
  seasonalLogTrend: FundingPressureBacktestModel,
): FundingPressureForecastModelId {
  const baselines = [lag1, seasonalLag4];
  const noWorse = (candidate: FundingPressureBacktestModel) => baselines.every((baseline) =>
    candidate.metrics.full.compositeScore <= baseline.metrics.full.compositeScore + 1e-12
    && candidate.metrics.recent8.compositeScore <= baseline.metrics.recent8.compositeScore + 1e-12
    && candidate.metrics.multiHorizon8.compositeScore <= baseline.metrics.multiHorizon8.compositeScore + 1e-12);
  const robustScore = (model: FundingPressureBacktestModel) =>
    (model.metrics.full.compositeScore
      + model.metrics.recent8.compositeScore
      + model.metrics.multiHorizon8.compositeScore) / 3;
  const promoted = [regimeBlend, seasonalLogTrend]
    .filter(noWorse)
    .sort((left, right) => robustScore(left) - robustScore(right))[0];
  if (promoted) return promoted.id as FundingPressureForecastModelId;
  return robustScore(lag1) <= robustScore(seasonalLag4) ? "lag-1" : "seasonal-lag-4";
}

function recursiveForecast(rows: AggregateRow[], model: FundingPressureModelId, horizons = 8) {
  const history = rows.map((row) => ({ ...row }));
  const predictions: Array<AggregateRow & { horizon: number }> = [];
  let period = rows.at(-1)!.period;
  for (let horizon = 1; horizon <= horizons; horizon += 1) {
    period = nextPeriod(period);
    const prediction = predict(model === "regime-blend" ? rows : history, period, model);
    const row = { period, ...prediction, horizon };
    predictions.push(row);
    if (model !== "regime-blend") history.push(row);
  }
  return predictions;
}

function buildMultiHorizonBacktestPoints(rows: AggregateRow[], model: FundingPressureModelId) {
  const points: FundingPressureBacktestPoint[] = [];
  for (let origin = MIN_TRAIN_QUARTERS; origin < rows.length; origin += 1) {
    const history = rows.slice(0, origin);
    if (history.filter((row) => row.period >= REGIME_START).length < MIN_POST_2023_TREND_ROWS) continue;
    for (let horizon = 1; horizon <= 8 && origin + horizon - 1 < rows.length; horizon += 1) {
      const prediction = recursiveForecast(history, model, horizon).at(-1)!;
      const observedRow = rows[origin + horizon - 1];
      const observed = actual(observedRow.cashCapex, observedRow.operatingCashFlow);
      const estimated = actual(prediction.cashCapex, prediction.operatingCashFlow);
      points.push({
        period: observedRow.period,
        actual: observed,
        predicted: estimated,
        ratioError: estimated.capexToOcf - observed.capexToOcf,
        fcfError: estimated.freeCashFlow - observed.freeCashFlow,
        capexLogError: Math.log(observed.cashCapex / Math.max(estimated.cashCapex, 1e-9)),
        ocfLogError: Math.log(observed.operatingCashFlow / Math.max(estimated.operatingCashFlow, 1e-9)),
      });
    }
  }
  if (!points.length) throw new Error(`${model}: no fair 1Q-8Q funding-pressure backtest points.`);
  return points;
}

interface HorizonErrors {
  ratio: number[];
  fcf: number[];
}

function horizonErrors(rows: AggregateRow[], model: FundingPressureModelId, horizon: number): HorizonErrors {
  const ratio: number[] = [];
  const fcf: number[] = [];
  for (let origin = MIN_TRAIN_QUARTERS; origin + horizon - 1 < rows.length; origin += 1) {
    if ((model === "epoch-log-linear" || model === "regime-blend")
      && rows.slice(0, origin).filter((row) => row.period >= REGIME_START).length < MIN_POST_2023_TREND_ROWS) continue;
    const path = recursiveForecast(rows.slice(0, origin), model, horizon);
    const prediction = actual(path.at(-1)!.cashCapex, path.at(-1)!.operatingCashFlow);
    const observedRow = rows[origin + horizon - 1];
    const observed = actual(observedRow.cashCapex, observedRow.operatingCashFlow);
    ratio.push(prediction.capexToOcf - observed.capexToOcf);
    fcf.push(prediction.freeCashFlow - observed.freeCashFlow);
  }
  return { ratio, fcf };
}

function jointHorizonRatioErrors(
  rows: AggregateRow[],
  model: FundingPressureForecastModelId,
  horizons: number,
) {
  const paths: number[][] = [];
  for (let origin = MIN_TRAIN_QUARTERS; origin + horizons - 1 < rows.length; origin += 1) {
    if (model === "regime-blend"
      && rows.slice(0, origin).filter((row) => row.period >= REGIME_START).length < MIN_POST_2023_TREND_ROWS) continue;
    const predictions = recursiveForecast(rows.slice(0, origin), model, horizons);
    paths.push(predictions.map((prediction, index) => {
      const estimated = actual(prediction.cashCapex, prediction.operatingCashFlow);
      const observedRow = rows[origin + index];
      const observed = actual(observedRow.cashCapex, observedRow.operatingCashFlow);
      return estimated.capexToOcf - observed.capexToOcf;
    }));
  }
  if (!paths.length) {
    throw new Error(`Funding-pressure crossing simulation has no complete ${horizons}-horizon OOS residual blocks.`);
  }
  return paths;
}

function rmse(values: number[]) {
  return Math.sqrt(values.reduce((sum, value) => sum + value ** 2, 0) / Math.max(1, values.length));
}

function interval(center: number, errors: number[], nonNegative: boolean): FundingPressureInterval {
  const absolute = errors.map(Math.abs).sort((left, right) => left - right);
  const rank = Math.ceil((absolute.length + 1) * INTERVAL_LEVEL);
  const calibrated = absolute.length >= MIN_CONFORMAL_RESIDUALS && rank <= absolute.length;
  const radius = calibrated
    ? absolute[rank - 1]
    : errors.length ? 1.645 * Math.sqrt(1 + 1 / errors.length) * rmse(errors) : Math.max(Math.abs(center) * 0.25, 0.1);
  return {
    low: nonNegative ? Math.max(0, center - radius) : center - radius,
    high: center + radius,
    level: calibrated ? 0.9 : null,
    source: calibrated ? "oos-conformal" : "rmse-fallback",
    calibrated,
    residualCount: errors.length,
    label: calibrated ? "90% OOS conformal" : "uncalibrated RMSE fallback",
  };
}

function scenarioValue(value: number, annualDeltaPct: number, horizon: number) {
  if (!finite(annualDeltaPct) || annualDeltaPct <= -100) {
    throw new Error("Funding-pressure annual scenario deltas must be finite and greater than -100%.");
  }
  return value * Math.pow(1 + annualDeltaPct / 100, horizon / 4);
}

function forecastPoints(
  rows: AggregateRow[],
  model: FundingPressureForecastModelId,
  scenario: Required<FundingPressureScenario>,
) {
  const raw = recursiveForecast(rows, model);
  return raw.map((point): FundingPressureForecastPoint => {
    const baseline = actual(point.cashCapex, point.operatingCashFlow);
    const cashCapex = scenarioValue(point.cashCapex, scenario.annualCapexGrowthDeltaPct, point.horizon);
    const operatingCashFlow = scenarioValue(point.operatingCashFlow, scenario.annualOcfGrowthDeltaPct, point.horizon);
    const current = actual(cashCapex, operatingCashFlow);
    const errors = horizonErrors(rows, model, point.horizon);
    return {
      period: point.period,
      horizon: point.horizon,
      cashCapex: current.cashCapex,
      operatingCashFlow: current.operatingCashFlow,
      capexToOcf: current.capexToOcf,
      freeCashFlow: current.freeCashFlow,
      ratioInterval: interval(current.capexToOcf, errors.ratio, true),
      fcfInterval: interval(current.freeCashFlow, errors.fcf, false),
      scenarioRange: {
        low: Math.min(baseline.capexToOcf, current.capexToOcf),
        high: Math.max(baseline.capexToOcf, current.capexToOcf),
        source: "scenario-range",
        label: "baseline-to-current scenario range",
      },
    };
  });
}

function quantile(values: number[], probability: number, tail: "lower" | "upper") {
  if (!values.length) throw new Error("Quantile requires at least one observation.");
  const sorted = [...values].sort((left, right) => left - right);
  const position = probability * (sorted.length - 1);
  const index = tail === "upper" ? Math.ceil(position) : Math.floor(position);
  return sorted[Math.min(sorted.length - 1, Math.max(0, index))];
}

function seededRandom(seed: number) {
  let state = seed >>> 0 || 0x9e3779b9;
  return () => {
    state ^= state << 13;
    state ^= state >>> 17;
    state ^= state << 5;
    return (state >>> 0) / 4_294_967_296;
  };
}

function crossing(
  latest: FundingPressureActual,
  points: FundingPressureForecastPoint[],
  rows: AggregateRow[],
  model: FundingPressureForecastModelId,
): FundingPressureCrossing {
  const pointCrossing = points.find((point) => point.capexToOcf >= 1)?.period ?? null;
  const firstCrossingIndices: number[] = [];
  const cumulativeCounts = Array(points.length).fill(0);
  const jointResidualPaths = jointHorizonRatioErrors(rows, model, points.length);
  const random = seededRandom(20_260_713);
  for (let path = 0; path < SIMULATION_PATHS; path += 1) {
    const residualPath = jointResidualPaths[Math.floor(random() * jointResidualPaths.length)];
    let first: number | null = null;
    for (let horizonIndex = 0; horizonIndex < points.length; horizonIndex += 1) {
      const error = residualPath[horizonIndex];
      if (first == null && points[horizonIndex].capexToOcf - error >= 1) first = horizonIndex;
    }
    if (first != null) {
      firstCrossingIndices.push(first);
      for (let index = first; index < points.length; index += 1) cumulativeCounts[index] += 1;
    }
  }
  const alreadyCrossed = latest.capexToOcf >= 1;
  const calibrated = alreadyCrossed || jointResidualPaths.length >= MIN_CONFORMAL_RESIDUALS;
  const status = alreadyCrossed ? "already-crossed" : pointCrossing ? "projected" : "censored";
  const conditionalQuarterInterval90 = calibrated && !alreadyCrossed && firstCrossingIndices.length
    ? [
      points[quantile(firstCrossingIndices, 0.05, "lower")].period,
      points[quantile(firstCrossingIndices, 0.95, "upper")].period,
    ] as [string, string]
    : null;
  return {
    status,
    pointEstimate: alreadyCrossed ? rows.at(-1)!.period : pointCrossing,
    censoredAfter: status === "censored" ? points.at(-1)!.period : null,
    probabilityWithinHorizon: calibrated ? (alreadyCrossed ? 1 : firstCrossingIndices.length / SIMULATION_PATHS) : null,
    probabilityByQuarter: points.map((point, index) => ({
      period: point.period,
      cumulativeProbability: calibrated ? (alreadyCrossed ? 1 : cumulativeCounts[index] / SIMULATION_PATHS) : null,
    })),
    conditionalQuarterInterval90,
    simulationPaths: SIMULATION_PATHS,
    residualBlocks: jointResidualPaths.length,
    calibrated,
    explanation: status === "censored"
      ? `The scenario point path does not cross before ${points.at(-1)!.period}; the result is right-censored. The simulated probability uses ${jointResidualPaths.length} complete OOS residual blocks${calibrated ? "." : " and is not calibrated."}`
      : `Crossing timing is scenario-conditional and uses ${jointResidualPaths.length} complete OOS residual blocks${calibrated ? "." : "; the probability is not calibrated."}`,
  };
}

function externalBenchmark(
  benchmark: EpochCashFlowBenchmark | undefined,
  companies: CompanySeries[],
): FundingPressureExternalBenchmark | null {
  if (!benchmark) return null;
  const comparableToPanel = companies.length === 5 && companies.some((company) => company.id === "oracle");
  return {
    scope: benchmark.validation.sourceScope,
    comparableToPanel,
    panelCompanyCount: companies.length,
    role: "cross-check-only",
    latestObserved: benchmark.observed.at(-1)!,
    trend: benchmark.trend,
    warning: comparableToPanel
      ? "Coverage matches the five-company set, but Epoch remains reconstructed cross-check evidence rather than SEC PIT input."
      : `Coverage mismatch: Epoch aggregates five hyperscalers while this panel has ${companies.length}; do not compare levels as like-for-like.`,
  };
}

export function runFundingPressureSlice(
  panel: DataPanel,
  options: FundingPressureOptions = {},
): FundingPressureSliceResult {
  const companies = selectedCompanies(panel);
  const { aggregate, rowsByCompany } = commonAggregate(companies);
  const commonPeriod = aggregate.at(-1)!.period;
  const monitorCompanies = companies.map((company): FundingPressureMonitorRow => {
    const row = rowsByCompany.get(company.id)!.find((item) => item.period === commonPeriod)!;
    const capexFiledFirst = filedDate(row, "capex");
    const ocfFiledFirst = filedDate(row, "ocf");
    const availableAt = [capexFiledFirst, ocfFiledFirst].sort().at(-1)!;
    return {
      id: company.id,
      name: company.name,
      period: commonPeriod,
      ...actual(row.capexAsFirstReported, row.operatingCashFlowFirstReported),
      watermark: {
        capexFiledFirst,
        ocfFiledFirst,
        availableAt,
        ageDays: ageDays(availableAt, panel.meta.asOf),
      },
    };
  });
  const aggregateActual = actual(
    monitorCompanies.reduce((sum, company) => sum + company.cashCapex, 0),
    monitorCompanies.reduce((sum, company) => sum + company.operatingCashFlow, 0),
  );

  const lag1 = buildBacktest(aggregate, "lag-1");
  const seasonalLag4 = buildBacktest(aggregate, "seasonal-lag-4");
  const epochLogLinear = buildBacktest(aggregate, "epoch-log-linear");
  const regimeBlend = buildBacktest(aggregate, "regime-blend");
  const seasonalLogTrend = buildBacktest(aggregate, "seasonal-log-trend");
  const selectedModel = selectModel(lag1, seasonalLag4, regimeBlend, seasonalLogTrend);
  const noScenario = { annualCapexGrowthDeltaPct: 0, annualOcfGrowthDeltaPct: 0 };
  const scenario = {
    annualCapexGrowthDeltaPct: options.scenario?.annualCapexGrowthDeltaPct ?? 0,
    annualOcfGrowthDeltaPct: options.scenario?.annualOcfGrowthDeltaPct ?? 0,
  };
  const baselinePoints = forecastPoints(aggregate, selectedModel, noScenario);
  const points = forecastPoints(aggregate, selectedModel, scenario);
  const benchmark = externalBenchmark(options.benchmark, companies);
  const scenarioActive = scenario.annualCapexGrowthDeltaPct !== 0 || scenario.annualOcfGrowthDeltaPct !== 0;

  return {
    monitor: {
      asOf: panel.meta.asOf,
      commonPeriod,
      companies: monitorCompanies,
      aggregate: aggregateActual,
      benchmarkCrossCheck: benchmark,
    },
    forecast: {
      commonPeriod,
      selectedModel,
      baselinePoints,
      points,
      horizons: { quarter1: points[0], quarter4: points[3], quarter8: points[7] },
      crossing: crossing(aggregateActual, points, aggregate, selectedModel),
      externalBenchmark: benchmark,
      scenarioImpact: {
        affectsCurrentChart: scenarioActive,
        directTargets: ["cash-capex", "operating-cash-flow"],
        historicalBacktestLocked: true,
        formula: "future metric(h) = baseline(h) × (1 + annual delta)^(h/4 quarters)",
        explanation: "Scenario deltas transform future CapEx and OCF only; the rolling-origin history is immutable.",
      },
    },
    backtest: {
      models: { lag1, seasonalLag4, epochLogLinear, regimeBlend, seasonalLogTrend },
      selectedModel,
      selection: {
        promotedComplexModel: selectedModel === "seasonal-log-trend" || selectedModel === "regime-blend",
        rule: "Promote the seasonal trend or anchored post-2023 regime blend only when it does not worsen versus both simple baselines in full, recent-8Q, and pooled direct 1Q-8Q loss; otherwise choose the stronger simple baseline. Epoch log-linear remains a cross-check-only challenger.",
      },
      historicalScenarioLocked: true,
    },
    methodology: {
      target: "Quarterly cash CapEx / operating cash flow and free cash flow for the aligned company aggregate.",
      vintagePolicy: "Company targets use SEC first-reported cash CapEx and OCF only; restatements, FMP, and Epoch never replace historical features.",
      seasonality: "Production candidates explicitly use either seasonal lag-4 or log-linear trend with calendar-quarter dummy variables.",
      validation: "Rolling-origin predictions compare identical quarters across lag-1, seasonal lag-4, Epoch-style log-linear, anchored post-2023 regime blend, and seasonal log trend; full, recent-8Q, and pooled direct 1Q-8Q metrics are reported.",
      selection: "A trend model is promoted only if its normalized joint Ratio/FCF loss does not degrade versus both simple baselines in the full, recent-8Q, and pooled direct 1Q-8Q windows; otherwise the lower robust average-loss baseline is used.",
      interval: "90% OOS conformal bands require at least nine genuine horizon-specific residuals; otherwise the band is labeled uncalibrated RMSE fallback. Scenario ranges are separate.",
      crossing: `First CapEx>=OCF timing uses fixed-seed joint-origin block resampling of complete direct-horizon OOS residual paths, preserving cross-horizon dependence. At least ${MIN_CONFORMAL_RESIDUALS} complete blocks are required before the probability is labeled calibrated; the result may be right-censored.`,
      scenario: "Annual CapEx and OCF deltas compound over h/4 years and affect future output only.",
    },
    sources: [
      {
        id: "sec-companyfacts",
        label: "SEC EDGAR Company Facts",
        url: "https://www.sec.gov/edgar/sec-api-documentation",
        role: "company target · first-reported vintage",
      },
      ...(options.benchmark ? [{
        id: options.benchmark.source.id,
        label: options.benchmark.source.label,
        url: options.benchmark.source.url,
        role: "five-company aggregate trend cross-check only",
      }] : []),
    ],
  };
}
